// INTELIMOVE-EXT-016 — Segmentação e atratividade de mercado.
// Cada segmento recebe um score de atratividade COMPOSTO e EXPLICÁVEL: um resultado por critério (nota 0–100 já
// orientada para "maior = mais atrativo", base — observado, declarado, inferido, manual ou sem dados —, explicação e
// evidências) e a composição ponderada pelos pesos que a equipe escolheu. Critério sem dados fica FORA da conta e reduz
// a cobertura; cobertura baixa vira "dados insuficientes" — nunca nota inventada. Tamanho e crescimento de mercado
// nunca são estimados: crescimento só aparece com evidência observada (sinais), e TAM/SAM/SOM fica "não estimado".
// A fórmula de composição mora AQUI para que a API e a tela (prévia dos pesos) calculem exatamente a mesma coisa.
import { z } from 'zod';

// ── Critérios ────────────────────────────────────────────────────────────────────────────────────────────────────────
export const SEGMENT_CRITERIA = [
  { key: 'ofertas', label: 'Aderência às ofertas', axis: 'ADERENCIA', weight: 15, hint: 'As suas ofertas confirmadas citam este público e as empresas do segmento têm aderência medida.' },
  { key: 'icp', label: 'Aderência aos ICPs', axis: 'ADERENCIA', weight: 15, hint: 'O segmento está nos ICPs confirmados (segmentos, CNAE, necessidades) e não nos critérios negativos.' },
  { key: 'demanda', label: 'Demanda observável', axis: 'MERCADO', weight: 10, hint: 'Sinais verificáveis de expansão, investimento, contratação ou tecnologia e análises de mercado com evidência.' },
  { key: 'crescimento', label: 'Crescimento', axis: 'MERCADO', weight: 5, hint: 'Somente indícios observados de crescimento (sinais de expansão e investimento). Nunca uma taxa estimada.' },
  { key: 'concorrencia', label: 'Intensidade competitiva', axis: 'MERCADO', weight: 10, hint: 'Quantos concorrentes confirmados atuam no segmento. Quanto menos, maior a nota.' },
  { key: 'entrada', label: 'Facilidade de entrada', axis: 'ADERENCIA', weight: 5, hint: 'Inferência: empresas nas regiões que você atende, relacionamento já iniciado e aderência das ofertas.' },
  { key: 'empresas', label: 'Disponibilidade de empresas', axis: 'MERCADO', weight: 10, hint: 'Empresas reais do segmento, na região escolhida, entre as suas empresas-alvo (rankings, artigos e diretórios não contam).' },
  { key: 'decisores', label: 'Disponibilidade de decisores', axis: 'MERCADO', weight: 5, hint: 'Pessoas públicas com papel de decisão mapeadas nas empresas do segmento.' },
  { key: 'sinais', label: 'Presença de sinais', axis: 'MERCADO', weight: 10, hint: 'Sinais verificáveis (com link e data) ligados às empresas ou ao tema do segmento nos últimos 180 dias.' },
  { key: 'barreiras', label: 'Barreiras', axis: 'ADERENCIA', weight: 5, hint: 'Restrições comerciais e critérios negativos declarados no contexto. Quanto menos barreiras, maior a nota.' },
  { key: 'risco', label: 'Risco', axis: 'MERCADO', weight: 5, hint: 'Empresas rejeitadas pela equipe e riscos ou ameaças citados nas análises. Quanto menor o risco, maior a nota.' },
  { key: 'evidencias', label: 'Qualidade das evidências', axis: 'NENHUM', weight: 5, hint: 'Parcela do que sustenta o segmento que tem fonte verificável (URL, data ou registro oficial).' },
] as const;
export type BuiltinCriterionKey = (typeof SEGMENT_CRITERIA)[number]['key'];
export const BUILTIN_CRITERION_KEYS = SEGMENT_CRITERIA.map((c) => c.key) as [BuiltinCriterionKey, ...BuiltinCriterionKey[]];
/** Eixo da matriz: ADERENCIA (capacidade de vencer) × MERCADO (atratividade do mercado); NENHUM entra só no score. */
export type CriterionAxis = 'ADERENCIA' | 'MERCADO' | 'NENHUM';

export interface SegmentCriterionConfig {
  key: string; label: string; description: string | null;
  enabled: boolean; weight: number;
  /** Critério incluído pela equipe: a nota de cada segmento é informada à mão, com justificativa. */
  custom: boolean; axis: CriterionAxis;
  /** Critério personalizado removido (fica no histórico das versões, fora da conta). */
  removed?: boolean;
}
export function defaultCriteria(): SegmentCriterionConfig[] {
  return SEGMENT_CRITERIA.map((c) => ({ key: c.key, label: c.label, description: c.hint, enabled: true, weight: c.weight, custom: false, axis: c.axis }));
}

export const CRITERION_BASES = ['OBSERVADO', 'DECLARADO', 'INFERIDO', 'MANUAL', 'SEM_DADOS'] as const;
export type CriterionBasis = (typeof CRITERION_BASES)[number];
export const CRITERION_BASIS_LABEL: Record<CriterionBasis, string> = {
  OBSERVADO: 'Observado', DECLARADO: 'Declarado no contexto', INFERIDO: 'Inferência', MANUAL: 'Avaliação da equipe', SEM_DADOS: 'Sem dados',
};
export const CRITERION_BASIS_HINT: Record<CriterionBasis, string> = {
  OBSERVADO: 'Contado a partir de registros com fonte (empresas, sinais, pessoas, análises).',
  DECLARADO: 'Vem do contexto confirmado pela sua empresa (ofertas, ICPs, restrições).',
  INFERIDO: 'Dedução do InteliMove a partir de outros dados — trate como hipótese.',
  MANUAL: 'Nota informada por uma pessoa da equipe, com justificativa.',
  SEM_DADOS: 'Não há dados suficientes: o critério fica fora da conta e reduz a cobertura.',
};

// ── Segmentos ────────────────────────────────────────────────────────────────────────────────────────────────────────
export const SEGMENT_KINDS = ['MERCADO', 'SEGMENTO', 'SUBSEGMENTO'] as const;
export type SegmentKind = (typeof SEGMENT_KINDS)[number];
export const SEGMENT_KIND_LABEL: Record<SegmentKind, string> = { MERCADO: 'Mercado', SEGMENTO: 'Segmento', SUBSEGMENTO: 'Subsegmento' };
export const SEGMENT_ORIGINS = ['ICP', 'CONTEXT', 'MARKET_ANALYSIS', 'TARGETS', 'CNAE', 'COMPETITOR', 'USER'] as const;
export type SegmentOrigin = (typeof SEGMENT_ORIGINS)[number];
export const SEGMENT_ORIGIN_LABEL: Record<SegmentOrigin, string> = {
  ICP: 'ICP confirmado', CONTEXT: 'Mercados do contexto', MARKET_ANALYSIS: 'Análise de mercado', TARGETS: 'Empresas-alvo',
  CNAE: 'Atividade econômica (CNAE) do ICP', COMPETITOR: 'Onde os concorrentes atuam', USER: 'Incluído pela equipe',
};
export const SEGMENT_DECISIONS = ['PENDING', 'CONFIRMED', 'REJECTED'] as const;
export type SegmentDecision = (typeof SEGMENT_DECISIONS)[number];

export const SEGMENT_CLASSES = ['PRIORITARIO', 'PROMISSOR', 'OBSERVAR', 'DADOS_INSUFICIENTES', 'NAO_RECOMENDADO'] as const;
export type SegmentClass = (typeof SEGMENT_CLASSES)[number];
export const SEGMENT_CLASS_LABEL: Record<SegmentClass, string> = {
  PRIORITARIO: 'Prioritário', PROMISSOR: 'Promissor', OBSERVAR: 'A observar', DADOS_INSUFICIENTES: 'Dados insuficientes', NAO_RECOMENDADO: 'Não recomendado',
};
export const SEGMENT_CLASS_HINT: Record<SegmentClass, string> = {
  PRIORITARIO: 'Score alto, boa cobertura e demanda ou empresas observadas. Concentre esforço aqui.',
  PROMISSOR: 'Bom potencial; falta comprovação ou parte dos critérios está mediana.',
  OBSERVAR: 'Potencial moderado. Acompanhe sinais antes de investir.',
  DADOS_INSUFICIENTES: 'Não há dados para concluir. Veja o que falta e como completar.',
  NAO_RECOMENDADO: 'Score baixo, conflito com restrição declarada ou rejeitado pela equipe.',
};

/** Limiares da classificação (documentados na tela). */
export const SEGMENT_THRESHOLDS = { prioritario: 70, promissor: 55, observar: 40, minCoverage: 0.5, minCriteria: 4, prioritarioCoverage: 0.65 } as const;

export interface SegmentEvidenceRefDto {
  kind: 'TARGET' | 'SIGNAL' | 'COMPETITOR' | 'MARKET_SEGMENT' | 'FINDING' | 'CONTEXT' | 'PERSON' | 'EVIDENCE';
  id: string | null; label: string; url: string | null; date: string | null;
}
export interface SegmentCriterionResultDto {
  key: string; score: number | null; basis: CriterionBasis; confidence: 'ALTA' | 'MEDIA' | 'BAIXA' | null;
  /** Valor legível do que foi medido ("3 empresas na região", "nenhum concorrente confirmado atua"). */
  measure: string; explanation: string;
  evidence: SegmentEvidenceRefDto[];
  /** Quem informou a nota (critério personalizado). */
  manual?: { by: string | null; at: string | null; justification: string | null } | null;
}
export interface SegmentContributionDto { key: string; label: string; weight: number; normalizedWeight: number; score: number; contribution: number; basis: CriterionBasis }
export interface SegmentScoreDto {
  score: number | null; coverage: number; criteriaWithData: number; criteriaEnabled: number;
  observedCriteria: number; inferredCriteria: number;
  contributions: SegmentContributionDto[]; missing: string[];
  axes: { aderencia: number | null; mercado: number | null };
  confidence: 'ALTA' | 'MEDIA' | 'BAIXA';
}

/** Composição do score — média ponderada só dos critérios habilitados COM dados; a cobertura é o peso que teve dados. */
export function composeSegmentScore(results: ReadonlyArray<Pick<SegmentCriterionResultDto, 'key' | 'score' | 'basis'>>, criteria: ReadonlyArray<SegmentCriterionConfig>): SegmentScoreDto {
  const active = criteria.filter((c) => c.enabled && !c.removed && c.weight > 0);
  const byKey = new Map(results.map((r) => [r.key, r]));
  const totalW = active.reduce((s, c) => s + c.weight, 0);
  const withData = active.filter((c) => { const r = byKey.get(c.key); return r && r.score !== null && r.basis !== 'SEM_DADOS'; });
  const dataW = withData.reduce((s, c) => s + c.weight, 0);
  const contributions: SegmentContributionDto[] = withData.map((c) => {
    const r = byKey.get(c.key)!;
    const nw = dataW ? c.weight / dataW : 0;
    return { key: c.key, label: c.label, weight: c.weight, normalizedWeight: round(nw, 4), score: r.score!, contribution: round(nw * r.score!, 2), basis: r.basis };
  }).sort((a, b) => b.contribution - a.contribution);
  const score = withData.length ? Math.round(contributions.reduce((s, x) => s + x.contribution, 0)) : null;
  const axis = (ax: CriterionAxis) => {
    const xs = withData.filter((c) => c.axis === ax);
    const w = xs.reduce((s, c) => s + c.weight, 0);
    return w ? Math.round(xs.reduce((s, c) => s + c.weight * byKey.get(c.key)!.score!, 0) / w) : null;
  };
  const observed = withData.filter((c) => ['OBSERVADO', 'DECLARADO', 'MANUAL'].includes(byKey.get(c.key)!.basis)).length;
  const coverage = totalW ? round(dataW / totalW, 3) : 0;
  const confidence = coverage >= 0.8 && observed >= 5 ? 'ALTA' : coverage >= 0.6 && observed >= 3 ? 'MEDIA' : 'BAIXA';
  return {
    score, coverage, criteriaWithData: withData.length, criteriaEnabled: active.length, observedCriteria: observed, inferredCriteria: withData.length - observed,
    contributions, missing: active.filter((c) => !withData.includes(c)).map((c) => c.label),
    axes: { aderencia: axis('ADERENCIA'), mercado: axis('MERCADO') }, confidence,
  };
}
const round = (v: number, d: number) => Math.round(v * 10 ** d) / 10 ** d;

export interface SegmentClassInput {
  score: SegmentScoreDto;
  /** Restrição declarada (restrição comercial ou critério negativo do ICP) que atinge o segmento. */
  blockedBy: string[];
  decision: SegmentDecision;
  /** Há empresas, sinais ou demanda OBSERVADOS (exigência para "prioritário"). */
  observedDemand: boolean;
}
/** Classe do segmento com o MOTIVO — a regra é a mesma na API e na prévia de pesos da tela. */
export function classifySegment(x: SegmentClassInput): { klass: SegmentClass; reason: string } {
  const T = SEGMENT_THRESHOLDS;
  if (x.decision === 'REJECTED') return { klass: 'NAO_RECOMENDADO', reason: 'Rejeitado pela equipe.' };
  // A decisão humana prevalece: segmento CONFIRMADO pela equipe não é barrado pela restrição (o conflito continua visível).
  if (x.blockedBy.length && x.decision !== 'CONFIRMED') return { klass: 'NAO_RECOMENDADO', reason: `Conflita com o que a sua empresa declarou: ${x.blockedBy.slice(0, 2).join('; ')}.` };
  const s = x.score;
  if (s.score === null || s.coverage < T.minCoverage || s.criteriaWithData < Math.min(T.minCriteria, s.criteriaEnabled)) {
    return { klass: 'DADOS_INSUFICIENTES', reason: `Só ${s.criteriaWithData} de ${s.criteriaEnabled} critérios têm dados (cobertura ${Math.round(s.coverage * 100)}%). Faltam: ${s.missing.slice(0, 4).join(', ')}${s.missing.length > 4 ? '…' : ''}.` };
  }
  if (s.score >= T.prioritario) {
    if (!x.observedDemand) return { klass: 'PROMISSOR', reason: `Score ${s.score}, mas sem empresas, sinais ou demanda observados — fica como promissor até haver comprovação.` };
    if (s.coverage < T.prioritarioCoverage) return { klass: 'PROMISSOR', reason: `Score ${s.score}, mas a cobertura (${Math.round(s.coverage * 100)}%) ainda é baixa para prioridade.` };
    return { klass: 'PRIORITARIO', reason: `Score ${s.score} com cobertura de ${Math.round(s.coverage * 100)}% e demanda ou empresas observadas.` };
  }
  if (s.score >= T.promissor) return { klass: 'PROMISSOR', reason: `Score ${s.score} (entre ${T.promissor} e ${T.prioritario - 1}).` };
  if (s.score >= T.observar) return { klass: 'OBSERVAR', reason: `Score ${s.score} (entre ${T.observar} e ${T.promissor - 1}).` };
  return { klass: 'NAO_RECOMENDADO', reason: `Score ${s.score} (abaixo de ${T.observar}).` };
}

// ── Pedidos ──────────────────────────────────────────────────────────────────────────────────────────────────────────
const txt = (max: number) => z.string().trim().max(max);
const justification = txt(1000).min(5, 'Escreva a justificativa (ao menos 5 caracteres).');
export const SegmentationRegionInput = z.object({
  areas: z.array(z.object({ uf: z.string().trim().max(2).nullable().optional(), city: txt(80).nullable().optional() }).strict()).max(12).optional(),
  text: txt(200).nullable().optional(),
}).strict();
export type SegmentationRegionInput = z.infer<typeof SegmentationRegionInput>;
export const CreateSegmentationRequest = z.object({
  name: txt(120).min(3),
  region: SegmentationRegionInput.optional(),
}).strict();
export type CreateSegmentationRequest = z.infer<typeof CreateSegmentationRequest>;
export const UpdateSegmentationRequest = z.object({
  version: z.number().int().min(1),
  name: txt(120).min(3).optional(),
  region: SegmentationRegionInput.optional(),
  criteria: z.array(z.object({ key: z.string().trim().min(2).max(60), enabled: z.boolean(), weight: z.number().int().min(0).max(100) }).strict()).max(40).optional(),
  status: z.enum(['ACTIVE', 'ARCHIVED']).optional(),
  justification: txt(1000).optional(),
}).strict();
export type UpdateSegmentationRequest = z.infer<typeof UpdateSegmentationRequest>;
export const CreateSegmentCriterionRequest = z.object({
  version: z.number().int().min(1),
  label: txt(80).min(3),
  description: txt(500).optional(),
  weight: z.number().int().min(1).max(100),
  axis: z.enum(['ADERENCIA', 'MERCADO', 'NENHUM']).default('NENHUM'),
  justification: txt(1000).optional(),
}).strict();
export type CreateSegmentCriterionRequest = z.infer<typeof CreateSegmentCriterionRequest>;
export const RemoveSegmentCriterionRequest = z.object({ version: z.number().int().min(1), justification: txt(1000).optional() }).strict();
export const CreateSegmentRequest = z.object({
  name: txt(120).min(2),
  kind: z.enum(SEGMENT_KINDS).default('SEGMENTO'),
  parentId: z.string().uuid().nullable().optional(),
  terms: z.array(txt(80).min(2)).max(12).optional(),
  cnaeCodes: z.array(z.string().trim().regex(/^[0-9]{2,7}$/, 'CNAE com 2 a 7 dígitos')).max(12).optional(),
  description: txt(1000).optional(),
  justification,
}).strict();
export type CreateSegmentRequest = z.infer<typeof CreateSegmentRequest>;
export const SegmentDecisionRequest = z.object({
  version: z.number().int().min(1),
  decision: z.enum(['CONFIRM', 'REJECT', 'RESET']),
  justification: txt(1000).optional(),
}).strict().superRefine((v, ctx) => {
  if (v.decision !== 'RESET' && (v.justification ?? '').length < 5) ctx.addIssue({ code: 'custom', path: ['justification'], message: 'Escreva a justificativa da decisão (ao menos 5 caracteres).' });
});
export type SegmentDecisionRequest = z.infer<typeof SegmentDecisionRequest>;
export const SegmentManualScoreRequest = z.object({
  version: z.number().int().min(1),
  criterionKey: z.string().trim().min(2).max(60),
  /** null = "sem dados" (remove a nota). */
  score: z.number().int().min(0).max(100).nullable(),
  justification,
}).strict();
export type SegmentManualScoreRequest = z.infer<typeof SegmentManualScoreRequest>;

// ── Respostas ────────────────────────────────────────────────────────────────────────────────────────────────────────
export interface SegmentLinksDto {
  targets: Array<{ id: string; name: string; uf: string | null; city: string | null; fitLevel: string; status: string; inRegion: boolean }>;
  targetsTotal: number;
  competitors: Array<{ id: string; name: string; basis: string }>;
  signals: Array<{ id: string; title: string; category: string; url: string | null; date: string; account: string }>;
  offerings: Array<{ id: string; name: string }>;
  icps: Array<{ id: string; name: string; relation: string }>;
}
export interface SegmentProfileDto {
  /** Distribuição das empresas observadas por UF (todas, não só da região escolhida). */
  regions: Array<{ uf: string; count: number }>;
  activities: Array<{ code: string; label: string; count: number }>;
  sizes: Array<{ label: string; count: number }>;
  /** Necessidades aparentes: dores/necessidades do ICP ligadas ao segmento e temas dos sinais (sempre com a origem). */
  needs: Array<{ text: string; origin: string }>;
}
export interface SegmentSizingDto {
  /** Nesta fase o InteliMove NÃO estima TAM/SAM/SOM; mostra a base observada e o que já existe para a análise futura. */
  status: 'NAO_ESTIMADO';
  observedCompanies: number; observedInRegion: number;
  claims: Array<{ text: string; method: string | null; factKind: string; hypothesis: boolean; analysisName: string; date: string | null; evidence: SegmentEvidenceRefDto[] }>;
  readiness: Array<{ item: string; ok: boolean; detail: string }>;
}
export interface SegmentRecommendationDto {
  key: string; kind: 'PRIORIZAR' | 'EXPLORAR_ESPACO' | 'VALIDAR' | 'COMPLETAR_DADOS' | 'EVITAR' | 'OBSERVAR';
  segmentId: string; segmentName: string;
  text: string; action: string;
  confidence: 'ALTA' | 'MEDIA' | 'BAIXA';
  /** Fonte de cada afirmação (com data). */
  sources: SegmentEvidenceRefDto[];
  /** Data do dado mais recente que sustenta a recomendação. */
  basedOnDate: string | null;
  generatedAt: string;
  inference: boolean;
}
export interface SegmentResultDto {
  id: string; key: string; name: string; kind: SegmentKind; parentId: string | null; parentName: string | null;
  origin: SegmentOrigin; originLabel: string; originDetail: string | null;
  terms: string[]; cnaeCodes: string[]; description: string | null;
  decision: SegmentDecision; decisionJustification: string | null; decidedAt: string | null; decidedByName: string | null;
  rank: number | null; klass: SegmentClass; klassReason: string; blockedBy: string[];
  criteria: SegmentCriterionResultDto[]; score: SegmentScoreDto;
  observedDemand: boolean;
  links: SegmentLinksDto; profile: SegmentProfileDto; sizing: SegmentSizingDto;
  /** Versão do registro do segmento (concorrência otimista nas decisões). */
  version: number;
}
export interface MarketSpaceDto { segmentId: string; segmentName: string; text: string; evidence: SegmentEvidenceRefDto[]; inference: boolean }
export interface CompetitiveGapDto { kind: 'FRAGILIDADE_DO_CONCORRENTE' | 'SEGMENTO_SEM_COBERTURA' | 'CONCORRENTE_FORA_DO_ICP'; segmentId: string | null; segmentName: string; text: string; competitors: string[]; evidence: SegmentEvidenceRefDto[]; inference: boolean }
export interface SegmentationBasisDto {
  generatedAt: string;
  contextVersion: number | null; contextConfirmed: boolean;
  offerings: number; icps: number; personas: number; competitorsConfirmed: number; competitorsWithSegments: number;
  targetsConsidered: number; targetsInRegion: number;
  /** Registros ignorados por não serem empresas (ranking, artigo, diretório, rede social) ou por terem sido rejeitados. */
  targetsIgnored: Array<{ reason: string; count: number }>;
  signalsVerifiable: number; people: number; marketSegments: number; marketAnalyses: number;
  regionLabel: string;
  notes: string[];
}
export interface SegmentationVersionDto {
  id: string; versionNo: number; trigger: 'CREATED' | 'RECALCULATED' | 'CONFIG' | 'SEGMENT' | 'MANUAL_SCORE';
  note: string | null; createdAt: string; createdByName: string | null;
  regionLabel: string; criteria: SegmentCriterionConfig[];
  counts: Partial<Record<SegmentClass, number>> & { total: number };
}
export interface SegmentationDetailDto {
  id: string; name: string; status: 'ACTIVE' | 'ARCHIVED'; version: number; createdAt: string; updatedAt: string;
  region: { label: string; input: SegmentationRegionInput };
  criteria: SegmentCriterionConfig[];
  viewing: SegmentationVersionDto; isCurrent: boolean; versions: SegmentationVersionDto[];
  basis: SegmentationBasisDto;
  segments: SegmentResultDto[];
  spaces: MarketSpaceDto[]; gaps: CompetitiveGapDto[];
  recommendations: SegmentRecommendationDto[];
  /** As decisões exibidas são as atuais (versão corrente) ou as gravadas na versão histórica. */
  editable: boolean; warnings: string[];
  /** Regiões sugeridas pelo contexto e pelas análises (para "alterar região"). */
  regionSuggestions: string[];
}
export interface SegmentationListItemDto {
  id: string; name: string; status: 'ACTIVE' | 'ARCHIVED'; regionLabel: string; versionNo: number; updatedAt: string;
  /** Estado ATUAL: classes da versão calculada com as decisões humanas de agora aplicadas (rejeitar, confirmar, voltar a pendente). */
  counts: Partial<Record<SegmentClass, number>> & { total: number };
  /** IM-EXT-018-C01: fotografia da versão calculada (imutável), para a área de versões. */
  versionCounts?: Partial<Record<SegmentClass, number>> & { total: number };
  top: Array<{ name: string; score: number | null; klass: SegmentClass }>;
}
export interface SegmentationCompareDto {
  from: SegmentationVersionDto; to: SegmentationVersionDto;
  changes: Array<{ segmentId: string; name: string; before: { score: number | null; klass: SegmentClass; rank: number | null } | null; after: { score: number | null; klass: SegmentClass; rank: number | null } | null }>;
  configChanges: string[];
}
export interface SegmentHistoryEntryDto { id: string; action: string; at: string; actor: string | null; justification: string | null; before: Record<string, unknown>; after: Record<string, unknown> }
